import type { ImageModelId } from "@fitness-app/data-models/ai/models";
import { getImageModelSelectOptions, imageModelTasks } from "@fitness-app/data-models/ai/tasks";
import { ExperimentOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { Button, Image, Input, message, Popover, Select, Skeleton, Space, Typography } from "antd";
import { useState } from "react";

import { getErrorMessage, isAxiosError } from "@fitness-app/utils";

import { analyticsService, nutritionApi } from "~/store/initializeStore";
import { type DishFormModel } from "./types";

export interface CurrentDishModel {
  steps: DishFormModel["preparationSteps"];
  ingredients: DishFormModel["ingredients"];
  name: DishFormModel["name"];
}

/** Kod z odpowiedzi 422 `GenerateImagesForDishesController` (nutrition-service), TRE-2054. */
const NO_IMAGE_RETURNED_ERROR_CODE = "NO_IMAGE_RETURNED";

const getDishImageErrorMessage = (error: unknown) => {
  if (isAxiosError<{ error?: string }>(error) && error.response?.data?.error === NO_IMAGE_RETURNED_ERROR_CODE) {
    return "Model nie zwrócił obrazka. Spróbuj ponownie lub wybierz inny model.";
  }

  return getErrorMessage(error);
};

const useDishAiGenerator = () => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async ({
      dishId,
      prompt,
      model,
      currentDishModel,
    }: {
      dishId: string;
      prompt: string;
      model?: ImageModelId;
      currentDishModel?: CurrentDishModel;
    }) => {
      setImageUrl(null);
      const response = await nutritionApi.post<{ url: string }>("/generate-image-for-dish", {
        prompt,
        dishId,
        model,
        currentDishModel,
      });
      setImageUrl(response.data.url);
    },
    onError: (error) => {
      message.error(getDishImageErrorMessage(error));
    },
    onSuccess: (_data, variables) => {
      analyticsService.track("generate_dish_image", {
        prompt: variables.prompt,
      });
    },
  });

  return {
    mutation,
    isLoading: mutation.isLoading,
    imageUrl,
  };
};

export const DishAiGenerator = ({
  dishId,
  currentDishModel,
  setImageUrl,
}: {
  dishId: string;
  setImageUrl: (url: string | null) => void;
  currentDishModel?: CurrentDishModel;
}) => {
  const [visible, setVisible] = useState(false);
  const [prompt, setPrompt] = useState("");
  // Domyślnie najtańszy model (~$0.034 za zdjęcie zamiast ~$0.067) — ten sam, którym leci masowa
  // generacja dla całego planu. Droższe warianty zostają na liście do wyboru.
  const [model, setModel] = useState<ImageModelId>(imageModelTasks.dishImage.default);

  const { mutation, isLoading, imageUrl } = useDishAiGenerator();

  return (
    <Popover
      open={visible}
      onOpenChange={setVisible}
      content={
        <Space direction="vertical" style={{ width: 400 }}>
          <Input.TextArea
            value={prompt}
            placeholder="Wpisz dodatkowo instrukcje dla AI (opcjonalnie)"
            onChange={(e) => setPrompt(e.target.value || "")}
          />

          <Select
            value={model}
            onChange={setModel}
            style={{ width: "100%" }}
            options={getImageModelSelectOptions("dishImage")}
          />

          <div className="flex justify-end gap-3">
            {imageUrl ? (
              <Button
                type="primary"
                onClick={() => {
                  setImageUrl(imageUrl);
                  setVisible(false);
                }}
              >
                Ustaw jako zdjęcie potrawy
              </Button>
            ) : null}
            <Button
              type={imageUrl ? "default" : "primary"}
              loading={isLoading}
              onClick={() => {
                mutation.mutate({
                  dishId,
                  prompt,
                  model,
                  currentDishModel,
                });
              }}
            >
              {imageUrl ? "Generuj na nowo" : "Generuj"}
            </Button>
          </div>

          <div className="my-4">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center gap-y-2">
                <Skeleton.Image className="rounded-md" active style={{ width: 150, height: 150 }} />
                <Typography.Text type="secondary" className="text-center text-sm">
                  Generowanie może zająć nawet około 1 minutę...
                </Typography.Text>
              </div>
            ) : imageUrl ? (
              <div className="flex items-center justify-center">
                <Image width={150} src={imageUrl} className="rounded-md" />
              </div>
            ) : null}
          </div>
        </Space>
      }
      title="Generuj zdjęcie"
      trigger="click"
      placement="bottom"
    >
      <Button icon={<ExperimentOutlined />}>Wygeneruj zdjęcie</Button>
    </Popover>
  );
};
